import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref as dbRef, get, remove } from 'firebase/database';
import { ref as storageRef, listAll, deleteObject } from 'firebase/storage';
import { database, storage } from '../../Firebase/firebaseconfig';
import { resolveTotal } from './billPDF';
import ConfirmDialog from './ConfirmDialog';
import { ArrowLeft, Eye, Trash2, FileText } from 'lucide-react';
import './admin-page.css';

export default function ViewQuotes() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const snap = await get(dbRef(database, 'quote'));
      if (snap.exists()) {
        const data = snap.val();
        const parseDate = (str) => {
          const parts = String(str || '').split(/[/\-.]/);
          if (parts.length === 3) {
            const [d, m, y] = parts.map(n => parseInt(n, 10));
            if (!isNaN(d) && !isNaN(m) && !isNaN(y)) return new Date(y, m - 1, d).getTime();
          }
          return 0;
        };
        const list = Object.entries(data).map(([quote, val]) => ({
          quote,
          ...val,
          resolvedTotal: resolveTotal(val),
        }));
        list.sort((a, b) => {
          const diff = parseDate(b.dateOfPurchase) - parseDate(a.dateOfPurchase);
          return diff !== 0 ? diff : Number(b.quote) - Number(a.quote);
        });
        setQuotes(list);
      } else {
        setQuotes([]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doDelete = async (quote) => {
    setRemoving(quote.quote);
    setConfirmTarget(null);
    try {
      try {
        const result = await listAll(storageRef(storage, `quotes/${quote.quote}`));
        await Promise.all(result.items.map(item => deleteObject(item)));
      } catch {}
      await remove(dbRef(database, `quote/${quote.quote}`));
      setQuotes(prev => prev.filter(q => q.quote !== quote.quote));
    } catch (err) {
      setError('Delete failed: ' + err.message);
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="ap-page ap-page-wide">
      <div className="ap-header">
        <button className="ap-back-btn" onClick={() => navigate('/admin/dashboard')}>
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="ap-title">All Quotations</h1>
          <p className="ap-subtitle">{quotes.length} quotations</p>
        </div>
      </div>

      {error && <div className="ap-error">{error}</div>}

      <div className="ap-search-wrap">
        <input
          className="ap-input"
          placeholder="Search by customer name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 360 }}
        />
      </div>

      <div className="ap-card">
        {loading && <div className="ap-loading">Loading…</div>}
        {!loading && quotes.length === 0 && (
          <div className="ap-empty">
            <FileText size={32} strokeWidth={1.2} style={{ marginBottom: 8, opacity: 0.4 }} />
            <div>No quotations found</div>
          </div>
        )}
        {!loading && quotes.length > 0 && (() => {
          const filtered = search.trim()
            ? quotes.filter(q => (q.customerName || '').toLowerCase().includes(search.toLowerCase()))
            : quotes;
          return filtered.length === 0
            ? <div className="ap-empty"><div>No quotations match "{search}"</div></div>
            : (
          <div className="ap-list">
            {filtered.map(quote => (
              <div className="ap-list-item" key={quote.quote} style={{ flexWrap: 'wrap', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="ap-list-item-name">{quote.customerName}</div>
                  <div style={{ fontSize: '0.76rem', color: '#6b7280', marginTop: 2 }}>
                    Quote #{quote.quote} &middot; {quote.dateOfPurchase} &middot;{' '}
                    <strong>₹{quote.resolvedTotal.toLocaleString('en-IN')}</strong>
                  </div>
                </div>
                <div className="ap-list-item-actions">
                  <button
                    className="ap-btn ap-btn-ghost"
                    style={{ padding: '6px 12px', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 5 }}
                    onClick={() => navigate(`/admin/dashboard/quote/${quote.quote}`)}
                  >
                    <Eye size={14} /> View
                  </button>
                  <button
                    className="ap-btn ap-btn-danger"
                    style={{ padding: '6px 12px', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 5 }}
                    disabled={removing === quote.quote}
                    onClick={() => setConfirmTarget(quote)}
                  >
                    <Trash2 size={14} /> {removing === quote.quote ? '…' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
            );
        })()}
      </div>

      {confirmTarget && (
        <ConfirmDialog
          title="Delete this quotation?"
          message={`Quote #${confirmTarget.quote} for "${confirmTarget.customerName}" will be permanently deleted. This cannot be undone.`}
          confirmLabel="Yes, Delete"
          confirmingLabel="Deleting…"
          confirming={removing === confirmTarget.quote}
          onConfirm={() => doDelete(confirmTarget)}
          onCancel={() => setConfirmTarget(null)}
        />
      )}
    </div>
  );
}
